import Grid from "@mui/material/Grid2";

import { useAlerts } from "../../../Components/hooks/cloudAnalytics/alerts/useAlerts";
import { ActiveAlertsCard } from "./Cards/ActiveAlertsCard";
import { ActiveBudgetsCard } from "./Cards/ActiveBudgetsCard";
import { ActiveCostAnomaliesCard } from "./Cards/ActiveCostAnomaliesCard";
import { AutomateCloudWasteCard } from "./Cards/AutomateCloudWasteCard";
import { CommitmentsCard } from "./Cards/CommitmentsCard";

type CloudCostsSectionProps = {
  cloudFlowsCount: number;
  cloudFlowsRisk: number;
  activeCommitmentsAtRiskCount: number;
  commitmentsCostAtRisk: number;
  loading: boolean;
  anomaliesAtRisk: number;
  numberOfAnomaliesInLast30Days: number;
  numberOfAnomalies: number;
  numberOfBudgets: number;
  numberOfBudgetsNearThreshold: number;
  budgetsAtRisk: number;
  totalCommitment: number;
  totalAmountOfActiveCommitments: number;
};

export const CloudCostsSection = ({
  cloudFlowsCount,
  cloudFlowsRisk,
  activeCommitmentsAtRiskCount,
  commitmentsCostAtRisk,
  loading,
  anomaliesAtRisk,
  numberOfAnomaliesInLast30Days,
  numberOfAnomalies,
  numberOfBudgets,
  numberOfBudgetsNearThreshold,
  budgetsAtRisk,
  totalCommitment,
  totalAmountOfActiveCommitments,
}: CloudCostsSectionProps) => {
  const [alertList, loadingAlerts] = useAlerts();

  // Determine if content is available
  const hasCommitmentsContent = activeCommitmentsAtRiskCount > 0 || totalCommitment > 0;
  const hasCloudWasteContent = cloudFlowsCount > 0;

  // Logic for conditional cards based on new requirements
  let showCommitmentsCard = false;
  let showCloudWasteCard = false;
  let showAlertsCard = false;

  if (hasCommitmentsContent && hasCloudWasteContent) {
    // Both have content - show both
    showCommitmentsCard = true;
    showCloudWasteCard = true;
  } else if (hasCommitmentsContent || hasCloudWasteContent) {
    // One has content - show that one + alerts
    showCommitmentsCard = hasCommitmentsContent;
    showCloudWasteCard = hasCloudWasteContent;
    showAlertsCard = true;
  } else {
    // Neither has content - show commitments + alerts
    showCommitmentsCard = true;
    showAlertsCard = true;
  }

  return (
    <Grid container spacing={2} sx={{ width: "100%" }} data-cy="cloud-costs-section">
      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <ActiveCostAnomaliesCard
          numberOfAnomalies={numberOfAnomalies}
          numberOfAnomaliesInLast30Days={numberOfAnomaliesInLast30Days}
          loading={loading}
          anomaliesAtRisk={anomaliesAtRisk}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <ActiveBudgetsCard
          numberOfBudgets={numberOfBudgets}
          numberOfBudgetsNearThreshold={numberOfBudgetsNearThreshold}
          loading={loading}
          totalCostAtRisk={budgetsAtRisk}
        />
      </Grid>

      {showCommitmentsCard && (
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <CommitmentsCard
            activeCommitmentsAtRiskCount={activeCommitmentsAtRiskCount}
            costAtRisk={commitmentsCostAtRisk}
            loading={loading}
            totalCommitment={totalCommitment}
            totalAmountOfActiveCommitments={totalAmountOfActiveCommitments}
          />
        </Grid>
      )}

      {showCloudWasteCard && (
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <AutomateCloudWasteCard cloudFlowsCount={cloudFlowsCount} totalPotentialSavings={cloudFlowsRisk} />
        </Grid>
      )}

      {showAlertsCard && (
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <ActiveAlertsCard alertList={alertList} loading={loadingAlerts} />
        </Grid>
      )}
    </Grid>
  );
};
